import { useCallback, useEffect, useMemo, useState } from "react";
import { FileWarning, Filter, Trash2, RotateCcw, ShieldAlert, Clock, CheckCircle, Download } from "lucide-react";
import api, { formatErr } from "../lib/api";
import { toast } from "sonner";
import { BRAND_COLORS, withAlpha } from "../lib/bookingStatus";
import { deDateMediumTime } from "../lib/dateFormat";

/**
 * Feb 2026 · DSGVO Art. 17 + §147 AO · Deletion-Audit-Trail
 * (5 Sep 2026 · User-Directive „§147 AO evidence trail one click away").
 *
 * Admin-only Table-View auf `deletion_audit`. Filter fuer Aktion,
 * Admin-E-Mail (Freitext) und Datumsbereich. Sortierung absteigend
 * nach Zeitstempel (Backend liefert bereits sortiert).
 */
const ACTION_LABEL = {
  admin_initiated: "Admin-Löschung ausgelöst",
  admin_reactivated: "Reaktiviert (Admin)",
  admin_executed: "Anonymisiert (Cron)",
  requested_by_self: "Nutzer-Antrag",
  requested_by_admin: "Admin-Antrag (Mail)",
  revoked: "Widerrufen",
  revoked_by_link: "Widerrufen (Link)",
  hard_removed_never_logged_in: "Sofort entfernt (nie eingeloggt)",
  executed: "Purge ausgeführt",
};

const ACTION_ICON = {
  admin_initiated: Trash2,
  admin_reactivated: RotateCcw,
  admin_executed: ShieldAlert,
  requested_by_self: Trash2,
  requested_by_admin: Trash2,
  revoked: RotateCcw,
  revoked_by_link: RotateCcw,
  hard_removed_never_logged_in: ShieldAlert,
  executed: ShieldAlert,
};

const ACTION_COLOR = {
  admin_initiated: "#8B1E1E",
  admin_reactivated: "#c9a961",
  admin_executed: "#8B1E1E",
  requested_by_self: "#8B1E1E",
  requested_by_admin: "#8B1E1E",
  revoked: "#3f8f5a",
  revoked_by_link: "#3f8f5a",
  hard_removed_never_logged_in: "#8B1E1E",
  executed: "#8B1E1E",
};

const EMPTY_FILTER = { action: "", admin_email: "", from: "", to: "" };

export default function TeamDeletionAudit() {
  const [rows, setRows] = useState([]);
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [f, setF] = useState(EMPTY_FILTER);

  // Filter werden explizit übergeben („Anwenden"), damit `load` stabil bleibt
  // und der Mount-Effekt nicht bei jeder Eingabe neu feuert.
  const load = useCallback(async (flt) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (flt.action) params.append("action", flt.action);
      if (flt.from) params.append("date_from", flt.from);
      if (flt.to) params.append("date_to", flt.to);
      const { data } = await api.get(`/admin/deletion-audit?${params.toString()}`);
      setRows(data);
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(EMPTY_FILTER); }, [load]);
  useEffect(() => {
    api.get("/admin/deletion-audit/actions").then(({ data }) => setActions(data)).catch(() => {});
  }, []);

  // Frontend-Filter fuer Admin-E-Mail (Freitext).
  const visibleRows = useMemo(() => {
    const q = f.admin_email.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) =>
        (r.admin_email || "").toLowerCase().includes(q) ||
        (r.admin_name || "").toLowerCase().includes(q),
    );
  }, [rows, f.admin_email]);

  const fmt = (iso) => {
    if (!iso) return "—";
    try {
      return deDateMediumTime(iso);
    } catch { return iso; }
  };

  return (
    <div className="max-w-[1200px] mx-auto px-6 md:px-10 py-10 md:py-14" data-testid="deletion-audit-page">
      <div className="mb-8">
        <div className="label-caps mb-2 flex items-center gap-2">
          <FileWarning className="w-3.5 h-3.5" strokeWidth={1.6} /> §147 AO · DSGVO Art. 17
        </div>
        <h1 className="font-display text-3xl md:text-4xl text-navy leading-tight">Löschungs-Audit</h1>
        <p className="text-[13px] text-navy-dark/70 leading-relaxed mt-2 max-w-2xl">
          Nachweis fuer jede angestoßene, widerrufene oder ausgefuehrte Konto-Loeschung.
          Filter nach Aktion, Admin und Zeitraum.
        </p>
      </div>

      <div
        className="bg-white border p-5 mb-6 grid grid-cols-1 md:grid-cols-4 gap-3"
        style={{ borderColor: withAlpha(BRAND_COLORS.navy, 0.12) }}
        data-testid="deletion-audit-filters"
      >
        <div>
          <label className="label-caps text-navy-dark/70 block mb-1.5">Aktion</label>
          <select
            data-testid="deletion-audit-filter-action"
            value={f.action}
            onChange={(e) => setF({ ...f, action: e.target.value })}
            className="w-full bg-white border border-navy/20 focus:border-gold focus:outline-none px-3 py-2 text-[13px] text-navy-dark"
          >
            <option value="">Alle</option>
            {actions.map((a) => (
              <option key={a} value={a}>{ACTION_LABEL[a] || a}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="label-caps text-navy-dark/70 block mb-1.5">Admin (E-Mail/Name)</label>
          <input
            data-testid="deletion-audit-filter-admin"
            value={f.admin_email}
            onChange={(e) => setF({ ...f, admin_email: e.target.value })}
            className="w-full bg-white border border-navy/20 focus:border-gold focus:outline-none px-3 py-2 text-[13px] text-navy-dark"
            placeholder="freie Suche"
          />
        </div>
        <div>
          <label className="label-caps text-navy-dark/70 block mb-1.5">Von</label>
          <input
            data-testid="deletion-audit-filter-from"
            type="date"
            value={f.from}
            onChange={(e) => setF({ ...f, from: e.target.value })}
            className="w-full bg-white border border-navy/20 focus:border-gold focus:outline-none px-3 py-2 text-[13px] text-navy-dark"
          />
        </div>
        <div>
          <label className="label-caps text-navy-dark/70 block mb-1.5">Bis</label>
          <input
            data-testid="deletion-audit-filter-to"
            type="date"
            value={f.to}
            onChange={(e) => setF({ ...f, to: e.target.value })}
            className="w-full bg-white border border-navy/20 focus:border-gold focus:outline-none px-3 py-2 text-[13px] text-navy-dark"
          />
        </div>
        <div className="md:col-span-4 flex items-center gap-3 pt-1">
          <button
            data-testid="deletion-audit-filter-apply"
            onClick={() => load(f)}
            className="inline-flex items-center gap-2 text-[11px] tracking-widest uppercase px-4 py-2 text-ivory"
            style={{ background: BRAND_COLORS.navy }}
          >
            <Filter className="w-3.5 h-3.5" strokeWidth={1.8} /> Anwenden
          </button>
          <button
            data-testid="deletion-audit-filter-reset"
            onClick={() => { setF(EMPTY_FILTER); load(EMPTY_FILTER); }}
            className="text-[11px] tracking-widest uppercase text-navy-dark/60 hover:text-navy underline underline-offset-4"
          >
            Zuruecksetzen
          </button>
          {/* Feb 2026 · §147 AO CSV-Export (5 Sep 2026 · User-Directive) */}
          <a
            data-testid="deletion-audit-csv-export"
            href={(() => {
              const params = new URLSearchParams();
              if (f.action) params.append("action", f.action);
              if (f.from) params.append("date_from", f.from);
              if (f.to) params.append("date_to", f.to);
              const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
              return `${base}/api/admin/deletion-audit.csv?${params.toString()}`;
            })()}
            target="_blank"
            rel="noreferrer"
            className="ml-auto inline-flex items-center gap-2 text-[11px] tracking-widest uppercase px-4 py-2 border transition-colors"
            style={{
              borderColor: BRAND_COLORS.gold,
              color: BRAND_COLORS.goldDark,
              background: withAlpha(BRAND_COLORS.gold, 0.08),
            }}
          >
            <Download className="w-3.5 h-3.5" strokeWidth={1.8} /> CSV exportieren
          </a>
        </div>
      </div>

      {loading ? (
        <div className="text-[13px] text-navy-dark/60 label-caps" data-testid="deletion-audit-loading">
          Wird geladen …
        </div>
      ) : visibleRows.length === 0 ? (
        <div
          className="bg-white border p-10 text-center"
          style={{ borderColor: withAlpha(BRAND_COLORS.navy, 0.1) }}
          data-testid="deletion-audit-empty"
        >
          <CheckCircle className="w-8 h-8 mx-auto mb-3 text-navy-dark/40" strokeWidth={1.4} />
          <div className="label-caps mb-1">Keine passenden Audit-Eintraege</div>
          <p className="text-[12px] text-navy-dark/60 mt-2">
            Aendern Sie die Filter, um mehr Zeilen zu sehen.
          </p>
        </div>
      ) : (
        <div className="bg-white border" style={{ borderColor: withAlpha(BRAND_COLORS.navy, 0.12) }}>
          <table className="w-full text-[13px]" data-testid="deletion-audit-table">
            <thead>
              <tr className="text-left border-b" style={{ borderColor: withAlpha(BRAND_COLORS.navy, 0.1) }}>
                <th className="px-4 py-3 label-caps">Zeitpunkt</th>
                <th className="px-4 py-3 label-caps">Aktion</th>
                <th className="px-4 py-3 label-caps">Betroffener</th>
                <th className="px-4 py-3 label-caps">Rolle</th>
                <th className="px-4 py-3 label-caps">Admin</th>
                <th className="px-4 py-3 label-caps">Details</th>
              </tr>
            </thead>
            <tbody>
              {visibleRows.map((r) => {
                const Icon = ACTION_ICON[r.action] || Clock;
                const color = ACTION_COLOR[r.action] || BRAND_COLORS.navyDark;
                const details = [];
                if (r.execute_at) details.push(`Fällig: ${fmt(r.execute_at)}`);
                if (r.has_invoices !== undefined) details.push(r.has_invoices ? "Rechnungen: ja" : "Rechnungen: nein");
                const ar = r.assignments_reset;
                if (ar) {
                  const parts = [];
                  if (ar.requests) parts.push(`${ar.requests} Anfragen`);
                  if (ar.bookings) parts.push(`${ar.bookings} Buchungen`);
                  if (ar.tasks) parts.push(`${ar.tasks} Aufgaben`);
                  if (parts.length) details.push(`Sweep: ${parts.join(" · ")}`);
                }
                if (r.mode) details.push(`Modus: ${r.mode}`);
                return (
                  <tr
                    key={r.id}
                    data-testid={`deletion-audit-row-${r.id}`}
                    className="border-b hover:bg-navy/[0.02]"
                    style={{ borderColor: withAlpha(BRAND_COLORS.navy, 0.06) }}
                  >
                    <td className="px-4 py-3 text-navy-dark/70 tabular-nums whitespace-nowrap">
                      {fmt(r.ts)}
                    </td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center gap-1.5 text-[11px] tracking-widest uppercase" style={{ color }}>
                        <Icon className="w-3.5 h-3.5" strokeWidth={1.6} />
                        {ACTION_LABEL[r.action] || r.action}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-navy-dark">
                      {r.email || <span className="text-navy-dark/40 italic">—</span>}
                      {r.email_hash && !r.email && (
                        <span className="text-navy-dark/40 text-[11px] font-mono block truncate max-w-[220px]">
                          hash: {r.email_hash.slice(0, 12)}…
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-navy-dark/70">{r.role || "—"}</td>
                    <td className="px-4 py-3 text-navy-dark/80">
                      {r.admin_name || r.admin_email || <span className="text-navy-dark/40 italic">System</span>}
                    </td>
                    <td className="px-4 py-3 text-navy-dark/70 text-[12px]">
                      {details.length ? details.join(" · ") : "—"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
